/**
 * Admin app presence — heartbeat interval and online threshold.
 */

import type { ClubRole } from './index';
import { COMMITTEE_ROLES, COACH_ROLES } from './clubRoles';

/** Heartbeat while an admin tab is open (ms). */
export const PRESENCE_HEARTBEAT_INTERVAL_MS = 60_000;

/** User counts as online if last_seen_at is within this window (minutes). */
export const PRESENCE_ONLINE_THRESHOLD_MINUTES = 5;

export const STAFF_ROLES: readonly ClubRole[] = [
  'platform_owner',
  ...COMMITTEE_ROLES,
  ...COACH_ROLES,
];

/** Committee or coach roles — excludes parent/swimmer-only accounts. */
export function isStaffMember(roles: ClubRole[]): boolean {
  return roles.some((r) => STAFF_ROLES.includes(r));
}

/** Human-readable relative time for presence UI. */
export function formatRelativeSeen(iso: string, nowMs = Date.now()): string {
  const sec = Math.max(0, Math.floor((nowMs - new Date(iso).getTime()) / 1000));
  if (sec < 15) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min === 1) return '1 min ago';
  if (min < 60) return `${min} mins ago`;
  const hr = Math.floor(min / 60);
  return hr === 1 ? '1 hr ago' : `${hr} hrs ago`;
}

/** Short label for admin route paths shown on the dashboard. */
export function adminPageLabel(path: string | null | undefined): string | null {
  if (!path) return null;
  const map: Record<string, string> = {
    '/app': 'Dashboard',
    '/app/sessions': 'Sessions',
    '/app/squads': 'Squads',
    '/app/people': 'People',
    '/app/finance': 'Finance',
    '/app/induction': 'Induction',
    '/app/users': 'Users',
    '/app/meets': 'Meets',
    '/app/communications': 'Communications',
    '/app/feedback': 'Feedback',
    '/app/update-notes': 'Update notes',
    '/app/website': 'Website',
    '/app/settings': 'Settings',
    '/app/import': 'Import',
    '/poolside': 'Poolside',
  };
  if (map[path]) return map[path];
  const tail = path.replace(/^\/app\//, '').split('/')[0];
  if (!tail) return 'Admin';
  return tail.charAt(0).toUpperCase() + tail.slice(1).replace(/-/g, ' ');
}
